import React, { Component, Fragment } from 'react';
import Table, { TableColumn } from "../../../Table";
import PageTitle from "../../../PageTitle";
import { TableRowProps } from "../../../TableRow";
import { CommandBarButton, TextField } from 'office-ui-fabric-react';
import { TenantRollover } from '../../../../models/rollover/TenantRollover';
import TableCell from '../../../TableCell';
import { openFileDownload } from '../../../../utils/Utils';
import {AllocateTenantRollover} from "../../../rollover/AllocateTenantRollover";
import {
    AllocateTenantRolloverData,
    AllocateTenantRolloverDataProvider
} from "../../../rollover/AllocateTenantRolloverDataProvider";
import {TARGET_ROLLOVER_YEAR} from "./TenantRolloverPage";
import {ICDBPageProps} from "../../../../utils/ICDBPageProps";
import {userAccessLevelIsAtLeast} from "../../../../models/UserAccessRecord";


interface RolloverListPageState {
    tenantRollovers: TenantRollover[];
    tenantRolloversFiltered: TenantRollover[];
    filterString: string;
    downloadingReport: boolean;
}

export default class RolloverListPage extends Component<ICDBPageProps, RolloverListPageState> {
    static displayName = RolloverListPage.name;

    private static readonly columns: TableColumn[] = [
        { title: "Tenant", propertyPath: "tenantName", enableSort: true, flex: 1 },
        { title: "School(s)", propertyPath: "schools", flex: 2 },
        { title: "Agent",propertyPath:"agent",flex: 1},
        { title: "Rollover Status", propertyPath: "status", flex: 1 },
    ];

    constructor(props: ICDBPageProps) {
        super(props);

        this.state = {
            tenantRollovers: [],
            tenantRolloversFiltered: [],
            filterString: "",
            downloadingReport: false,
        }
    }

    async componentDidMount() {
        await this.load();
    }

    async componentDidUpdate(prevProps: Readonly<ICDBPageProps>, prevState: RolloverListPageState): Promise<void> {
        if (this.state.filterString !== prevState.filterString) {
            this.filter();
        }
    }

    private async load() {
        try {
            const tenantRollovers = await this.props.apiService.getRolloversForTenants();
            this.setState({
                tenantRollovers
            });
            this.filter();
        }
        catch (e) {
            this.props.messageBar.error(`Error loading tenants for rollover. ${e.message}`);
        }
    }

    private filter() {
        const filtered = this.state.tenantRollovers.filter((tenantRollover) => {
            if (tenantRollover.tenantName.toLowerCase().indexOf(this.state.filterString!) !== -1) {
                return true;
            }
            else if (tenantRollover.schools.some(school => school.schoolCode.toLowerCase().indexOf(this.state.filterString!) !== -1)) {
                return true;
            }
            else if (tenantRollover.schools.some(school => school.schoolName.toLowerCase().indexOf(this.state.filterString!) !== -1)) {
                return true;
            }
            else if (tenantRollover.schools.some(school => school.licenseRef != null && school.licenseRef.toLowerCase().indexOf(this.state.filterString!) !== -1)) {
                return true;
            }
            else {
                return false;
            }
        });
        this.setState({
            tenantRolloversFiltered: filtered
        });
    }

    rowPropsForItem = (item: TenantRollover): TableRowProps => {
        return {
            enableRowLink: true,
            to: `/cdb-admin/rollover/tenant/${item.tenantId}`
        };
    }

    private columnsProps = (item: TenantRollover, tableColumn: TableColumn, index: number,data: AllocateTenantRolloverData) => {
        switch (tableColumn.propertyPath) {
            case "tenantName":
                return <TableCell key={index} flex={tableColumn.flex}>
                    <span>{item.tenantName}</span>
                    <span className={"cdb-table__extra-detail"}>{item.tenantId}</span>
                </TableCell>;
            case "schools":
                return <TableCell key={index} flex={tableColumn.flex}>
                    <span>{item.schools.map(school => school.schoolName).join(", ")}</span>
                    <span className={"cdb-table__extra-detail"}>{item.schools.map(school => school.licenseRef).join(", ")}</span>
                </TableCell>;
            case "agent":
                
                const allocateEnabled = userAccessLevelIsAtLeast("admin", this.props.usersCdbAccessLevel)
                
                return (
                    <TableCell key={index} flex={tableColumn.flex}>
                        <AllocateTenantRollover 
                            tenantId={item.tenantId}
                            academicYear={TARGET_ROLLOVER_YEAR}
                            currentAgentId={item.allocatedToId}
                            availableAgentIds={data.availableAgentIds}
                            agentUserDict={data.agentUserDict}
                            disabled={!allocateEnabled}
                            apiService={this.props.apiService}
                            messageBar={this.props.messageBar}/>
                    </TableCell>
                );
            case "status":
                return <TableCell key={index} flex={tableColumn.flex}>
                    {this.statusPill(item)}
                </TableCell>;
        }
    }

    private statusPill(item: TenantRollover) {
        const schoolsRolledOver = item.schools.filter(s => s.isRolledOver).length;
        const schoolsCount = item.schools.length;
        const colour = schoolsCount === schoolsRolledOver ? "green" : "amber"
        return <div className={`pill pill--${colour}`}>{schoolsRolledOver}/{schoolsCount}</div>
    }

    private downloadReport = async () => {
        if (this.state.downloadingReport !== true) {
            this.setState({ downloadingReport: true });
            try {
                var report = await this.props.apiService.downloadRolloverReport();
                await openFileDownload(report);
            }
            catch (e) {
                this.props.messageBar.error(`Error downloading report. ${e.message}`);
            }
            this.setState({ downloadingReport: false });
        }
    }
    
    renderPage(data: AllocateTenantRolloverData): JSX.Element
    {
        return (
            <Fragment>
                <PageTitle title={"Rollovers"} />

                <div className="filter-controls p-top-1rem">
                    <div className="filter-controls__row">
                        <TextField className="filter-controls__input" autoFocus label="Tenant Name" placeholder="Filter"
                                   onKeyUp={(keyEvent) => {
                                       if(keyEvent.key === "Enter")
                                       {
                                           if(this.state.tenantRolloversFiltered?.length === 1)
                                           {
                                               const filteredRollover = this.state.tenantRolloversFiltered[0];
                                               this.props.history.push(`/cdb-admin/rollover/tenant/${filteredRollover.tenantId}`);
                                           }
                                       }
                                   }}
                                   onChange={
                                       (event, newValue) => {
                                           this.setState({
                                               filterString: newValue != undefined ? newValue.toLowerCase() : ""
                                           });
                                       }
                                   } />
                    </div>
                </div>

                <Table
                    title={"Rollovers"}
                    items={this.state.tenantRolloversFiltered}
                    columns={RolloverListPage.columns}
                    enableSort={false}
                    rowPropsForItem={this.rowPropsForItem}
                    cellForColumn={(item,column,index) => this.columnsProps(item as TenantRollover,column,index,data)}>

                    <CommandBarButton
                        iconProps={{ iconName: "download" }}
                        text="Download Report (Legacy)"
                        disabled={this.state.downloadingReport === true}
                        onClick={this.downloadReport}
                    />

                </Table>
            </Fragment>
        )
    }

    render() {
        return (
            <Fragment>
                <AllocateTenantRolloverDataProvider apiService={this.props.apiService} graphClient={this.props.graphClient}>
                    {(data: AllocateTenantRolloverData) => {
                        return this.renderPage(data);
                    }}
                </AllocateTenantRolloverDataProvider>
            </Fragment>
        );
    }
}